import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { workbooksApi, WorkbookLite } from '@/lib/workbooks';
import { AppShell } from '@/components/app/AppShell';
import { ConfirmModal } from '@/components/ConfirmModal';

export function WorkbooksPage() {
  const { t, i18n } = useTranslation();
  const { wsId } = useParams<{ wsId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: workbooks = [], isLoading } = useQuery({
    queryKey: ['workbooks', wsId],
    queryFn: () => workbooksApi.list(wsId!),
    enabled: !!wsId,
  });

  const create = useMutation({
    mutationFn: () => workbooksApi.create(wsId!, t('workbook.name_default')),
    onSuccess: (wb) => {
      qc.invalidateQueries({ queryKey: ['workbooks', wsId] });
      navigate(`/app/workspace/${wsId}/workbook/${wb.id}`);
    },
  });

  const del = useMutation({
    mutationFn: (id: string) => workbooksApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workbooks', wsId] }),
  });

  const rename = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => workbooksApi.rename(id, name),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workbooks', wsId] }),
  });

  // Pending delete — id of the row the user clicked Delete on, or null.
  // Rendered as a ConfirmModal at the bottom of the page; OK fires del.mutate.
  const [pendingDelete, setPendingDelete] = useState<WorkbookLite | null>(null);

  return (
    <AppShell title={t('workbook.title')} subtitle={t('workbook.subtitle')}>
      <main className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-semibold text-foreground tracking-tight">{t('workbook.title')}</h1>
            <p className="mt-1 text-muted-foreground max-w-2xl">{t('workbook.subtitle')}</p>
          </div>
          <button
            onClick={() => create.mutate()}
            disabled={create.isPending || !wsId}
            className="h-9 px-3.5 inline-flex items-center rounded-md bg-foreground text-background text-[12.5px] font-semibold hover:bg-foreground/90 disabled:opacity-60 transition-colors"
          >
            {create.isPending ? t('workbook.creating') : t('workbook.new')}
          </button>
        </div>

        {isLoading ? (
          <div className="text-muted-foreground text-sm">{t('common.loading')}</div>
        ) : workbooks.length === 0 ? (
          <div className="rounded-md border border-dashed border-border bg-card p-12 text-center">
            <p className="text-foreground font-medium">{t('workbook.empty')}</p>
          </div>
        ) : (
          <div className="rounded-md border border-border bg-card overflow-hidden divide-y divide-border">
            {workbooks.map((wb) => (
              <WorkbookRow
                key={wb.id}
                wb={wb}
                wsId={wsId!}
                locale={i18n.language}
                onDelete={() => setPendingDelete(wb)}
                onRename={(name) => rename.mutateAsync({ id: wb.id, name })}
                t={t}
              />
            ))}
          </div>
        )}
      </main>

      <ConfirmModal
        open={pendingDelete !== null}
        title={t('workbook.delete_confirm')}
        body={pendingDelete?.name}
        confirmLabel={t('common.delete')}
        destructive
        busy={del.isPending}
        onConfirm={() => {
          if (pendingDelete) {
            del.mutate(pendingDelete.id, { onSettled: () => setPendingDelete(null) });
          }
        }}
        onCancel={() => setPendingDelete(null)}
      />
    </AppShell>
  );
}

function WorkbookRow({
  wb, wsId, locale, onDelete, onRename, t,
}: {
  wb: WorkbookLite;
  wsId: string;
  locale: string;
  onDelete: () => void;
  onRename: (name: string) => Promise<unknown>;
  t: (k: string, opts?: Record<string, unknown>) => string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(wb.name);
  const [saving, setSaving] = useState(false);

  async function save() {
    const next = draft.trim();
    if (!next || next === wb.name) { setEditing(false); return; }
    setSaving(true);
    try {
      await onRename(next);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  if (editing) {
    return (
      <div className="flex items-center justify-between gap-4 px-5 py-3 bg-foreground/[0.03]">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void save();
            if (e.key === 'Escape') { setDraft(wb.name); setEditing(false); }
          }}
          autoFocus
          className="flex-1 min-w-0 rounded-md border border-border bg-white px-3 py-1.5 text-sm font-medium"
        />
        <button onClick={() => { setDraft(wb.name); setEditing(false); }} disabled={saving} className="text-[11px] text-muted-foreground hover:text-foreground px-2 py-1">
          {t('common.cancel')}
        </button>
        <button onClick={save} disabled={saving} className="text-[11px] font-semibold text-emerald-700 hover:text-emerald-800 px-2 py-1 disabled:opacity-60">
          {saving ? t('settings.saving') : t('common.save')}
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between gap-4 px-5 py-3 hover:bg-foreground/[0.02]">
      <Link
        to={`/app/workspace/${wsId}/workbook/${wb.id}`}
        className="flex-1 min-w-0"
      >
        <div className="font-medium text-foreground truncate">{wb.name}</div>
        <div className="num text-[11px] text-muted-foreground">
          {new Date(wb.updatedAt).toLocaleString(locale)}
        </div>
      </Link>
      <button
        onClick={() => setEditing(true)}
        className="text-[11px] text-muted-foreground hover:text-foreground px-2 py-1 rounded-md"
      >
        {t('common.edit')}
      </button>
      <button
        onClick={onDelete}
        className="text-[11px] text-muted-foreground hover:text-rose-700 px-2 py-1 rounded-md"
      >
        {t('workbook.delete')}
      </button>
    </div>
  );
}
